import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast";
import LoadingSpinner from "../components/LoadingSpinner";
import MetricCard from "../components/MetricCard";
import ConversationCard from "../components/ConversationCard";
import EmptyState from "../components/EmptyState";
import useWidget from "../hooks/useWidget";
import { analyticsApi } from "../services/api";
import errorTracker from "../utils/errorTracker";
import { getErrorMessage } from "../utils/formatters";
import { ANALYTICS } from "../config/constants";
import "./WidgetAnalytics.css";

const WidgetAnalytics = () => {
  const { id: widgetId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Use the custom hook for widget loading
  const { widget, loading: widgetLoading } = useWidget(widgetId);

  const [analytics, setAnalytics] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Date range state (default: last 30 days)
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  );

  const loadAnalytics = useCallback(
    async (signal) => {
      setLoadingAnalytics(true);
      try {
        const response = await analyticsApi.getWidgetAnalytics(
          widgetId,
          startDate,
          endDate,
          { signal },
        );
        setAnalytics(response);
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }
        errorTracker.captureException(err, {
          context: "loadAnalytics",
          widgetId,
        });
        toast.error(getErrorMessage(err, "Failed to load analytics"));
      } finally {
        setLoadingAnalytics(false);
      }
      // INTENTIONAL: toast is a stable reference from useToast() context
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [widgetId, startDate, endDate, toast],
  );

  const loadRecentConversations = useCallback(
    async (signal) => {
      setLoadingConversations(true);
      try {
        const response = await analyticsApi.getConversations(widgetId, 1, 5, {
          signal,
        });
        setConversations(response.conversations || []);
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }
        errorTracker.captureException(err, {
          context: "loadConversations",
          widgetId,
        });
        // Don't show toast (conversations are optional), already tracked in errorTracker
      } finally {
        setLoadingConversations(false);
      }
      // INTENTIONAL: No toast used here, but keeping consistent pattern
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [widgetId],
  );

  // Ref to track the AbortController for manual date range refreshes
  const dateRangeControllerRef = useRef(null);

  useEffect(() => {
    if (!widgetId) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const loadData = async () => {
      await Promise.all([
        loadAnalytics(signal),
        loadRecentConversations(signal),
      ]);
    };

    loadData();

    return () => {
      controller.abort();
      // Also abort any in-flight manual date range refresh
      if (dateRangeControllerRef.current) {
        dateRangeControllerRef.current.abort();
      }
    };
  }, [widgetId, loadAnalytics, loadRecentConversations]);

  const handleDateRangeChange = () => {
    // Validate date range
    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    const today = new Date().toISOString().split("T")[0];
    if (endDate > today) {
      toast.error("End date cannot be in the future");
      return;
    }

    // Abort any previous manual refresh before starting a new one
    if (dateRangeControllerRef.current) {
      dateRangeControllerRef.current.abort();
    }
    const controller = new AbortController();
    dateRangeControllerRef.current = controller;
    loadAnalytics(controller.signal);
  };

  const handleViewConversation = (conversation) => {
    navigate(`/widgets/${widgetId}/conversations/${conversation.id}`);
  };

  const handleViewAllConversations = () => {
    navigate(`/widgets/${widgetId}/conversations`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value || 0);
  };

  if (widgetLoading) {
    return (
      <div className="dashboard-home">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="widget-analytics">
      <div className="analytics-header">
        <div className="analytics-header-content">
          <h1>Analytics</h1>
          <p className="subtitle">
            {widget ? `Analytics for "${widget.name}"` : "Widget Analytics"}
          </p>
        </div>
        <button
          onClick={() => navigate(`/widgets/${widgetId}`)}
          className="btn btn-secondary"
        >
          Back to Widget
        </button>
      </div>

      <div className="analytics-filters">
        <div
          className="date-range-selector"
          role="search"
          aria-label="Date range filter"
        >
          <label htmlFor="analytics-start-date">Start Date:</label>
          <input
            id="analytics-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate}
            aria-label="Start date for analytics range"
          />

          <label htmlFor="analytics-end-date">End Date:</label>
          <input
            id="analytics-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            max={new Date().toISOString().split("T")[0]}
            aria-label="End date for analytics range"
          />

          <button
            onClick={handleDateRangeChange}
            className="btn btn-primary"
            aria-label="Update analytics for selected date range"
          >
            Update
          </button>
        </div>
      </div>

      {loadingAnalytics ? (
        <LoadingSpinner />
      ) : analytics ? (
        <>
          <div
            className="analytics-metrics"
            role="region"
            aria-label="Key metrics summary"
          >
            <MetricCard
              icon="💬"
              label="Total Messages"
              value={analytics.totals?.messages?.toLocaleString() || "0"}
              color="blue"
            />
            <MetricCard
              icon="🗨️"
              label="Conversations"
              value={analytics.totals?.conversations?.toLocaleString() || "0"}
              color="green"
            />
            <MetricCard
              icon="🎯"
              label="Total Tokens"
              value={analytics.totals?.totalTokens?.toLocaleString() || "0"}
              color="purple"
            />
            <MetricCard
              icon="💰"
              label="Estimated Cost"
              value={formatCurrency(analytics.totals?.estimatedCost)}
              color="orange"
            />
          </div>

          {analytics.totals?.messages > 0 && (
            <div className="analytics-charts">
              <div className="chart-container">
                <h3>Usage Over Time</h3>
                <div className="chart-data-table">
                  <table role="table" aria-label="Daily usage statistics">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Messages</th>
                        <th>Conversations</th>
                        <th>Tokens</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.dailyUsage
                        ?.slice(0, ANALYTICS.DAILY_USAGE_TABLE_LIMIT)
                        .map((day) => (
                          <tr key={day.date}>
                            <td>{new Date(day.date).toLocaleDateString()}</td>
                            <td>{day.messages}</td>
                            <td>{day.conversations}</td>
                            <td>{day.tokens?.toLocaleString()}</td>
                            <td>{day.cost}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {analytics.dailyUsage?.length >
                    ANALYTICS.DAILY_USAGE_TABLE_LIMIT && (
                    <p className="table-note">
                      Showing most recent {ANALYTICS.DAILY_USAGE_TABLE_LIMIT}{" "}
                      days. Total: {analytics.dailyUsage.length} days
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}

      <div className="recent-conversations-section">
        <div className="section-header">
          <h3>Recent Conversations</h3>
          <button onClick={handleViewAllConversations} className="btn btn-link">
            View All →
          </button>
        </div>

        {loadingConversations ? (
          <LoadingSpinner />
        ) : conversations.length > 0 ? (
          <div className="conversations-list">
            {conversations.map((conversation) => (
              <ConversationCard
                key={conversation.id}
                conversation={conversation}
                onClick={handleViewConversation}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            message="No conversations yet"
            subtitle="Conversations will appear here once users start chatting with your widget"
          />
        )}
      </div>
    </div>
  );
};

export default WidgetAnalytics;
